import { Dayts, sleep } from '@/utils';
import { Import, ImportStatusEnum, ImportWithAirportStatusEnum, ImportWithAirportTypeEnum } from '@/models/gen/graphql';
import { deleteSessionStorage, getSessionStorage, setSessionStorage } from '@/hooks/useSessionStorage';

import { DATE_FE_FORMAT } from '@/constants';
import { InitComponentStateType } from '@/state';
import { searchAddonTableWithMetadata } from '@/features/Addons/api/services/searchAddonTable';

export namespace AddonsTypes {
  export type Filters = {
    from: string;
    to: string;
    createdBy: string;
    airportCode: string[];
    providerId: string;
    type?: ImportWithAirportTypeEnum;
    status?: ImportWithAirportStatusEnum[];
    archived?: boolean;
  };
  export const initFilters: Filters = {
    from: new Dayts().startOf('day').format(DATE_FE_FORMAT),
    to: new Dayts().endOf('day').format(DATE_FE_FORMAT),
    createdBy: '',
    airportCode: [],
    providerId: '',
    type: ImportWithAirportTypeEnum.Addon,
    status: [
      ImportWithAirportStatusEnum.Ingesting,
      ImportWithAirportStatusEnum.NotParsed,
      ImportWithAirportStatusEnum.Parsed,
      ImportWithAirportStatusEnum.Potential,
      ImportWithAirportStatusEnum.Staged,
      ImportWithAirportStatusEnum.Staging,
      ImportWithAirportStatusEnum.Uploaded,
    ],
    archived: false,
  };
  export type Modals = {
    addonUpload: {
      show: boolean;
    } | null;
    addonOverrides: {
      show: boolean;
      importId: string;
      name?: string;
    } | null;
    addonSettings: {
      show: boolean;
    } | null;
  };
  export const initModals: Modals = {
    addonUpload: null,
    addonOverrides: null,
    addonSettings: null,
  };
  export type Settings = {
    rememberFilters: boolean;
    addonAsDrawer: boolean;
    columns: {
      SELECT: boolean;
      DATE: boolean;
      STATUS: boolean;
      PROVIDER: boolean;
      IMPORTED_BY: boolean;
      FROM: boolean;
      SUBJECT: boolean;
      TYPE: boolean;
      DOWNLOAD: boolean;
    };
  };

  export type State = {
    imports: Import[];
    total: number;
    selected: string[];
    loading: boolean;
    filters: Filters;
    table: Table;
    modals: Modals;
    settings: Settings;
    fetch: () => Promise<void>;
    onSelect: (id: string) => void;
    onReset: () => void;
    expandedId: string;
    scrollToFocus: () => void;
    setFocus: (id: string) => void;
    focused: string[];
  };
  export const initSettings: Settings = {
    rememberFilters: true,
    addonAsDrawer: false,
    columns: {
      SELECT: true,
      DATE: true,
      STATUS: true,
      PROVIDER: true,
      IMPORTED_BY: true,
      FROM: true,
      SUBJECT: true,
      TYPE: true,
      DOWNLOAD: true,
    },
  };

  export const FOCUS_STORAGE_KEY = 'Focus::AddonTable';
  export const scrollToFocus = (): string[] => {
    const focus = getSessionStorage<{
      currentElementId: string;
      nextElementId: string;
      previousElementId: string;
    }>(FOCUS_STORAGE_KEY, undefined);
    if (!focus) return;
    // get the dom node
    const node =
      document.getElementById(focus.currentElementId) ||
      document.getElementById(focus.nextElementId) ||
      document.getElementById(focus.previousElementId);
    if (!node) return;
    // focus the next import
    node.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
    // clear focus from session storage
    deleteSessionStorage(FOCUS_STORAGE_KEY);
    return [node.id];
  };

  export const setFocus = (elementId: string): void => {
    const node = document.getElementById(elementId);
    const previousSibling = node?.previousElementSibling;
    const nextSibling = node?.nextElementSibling;
    setSessionStorage(FOCUS_STORAGE_KEY, {
      currentElementId: elementId,
      nextElementId: nextSibling?.id || '',
      previousElementId: previousSibling?.id || '',
    });
  };

  export const initState: InitComponentStateType<State> = (setState, getState) => {
    const setFocus = (id = getState().state.selected[0]) => {
      AddonsTypes.setFocus(id);
      setState((current) => ({ ...current, focused: [] }));
    };
    const scrollToFocus = () => {
      const ids = AddonsTypes.scrollToFocus();
      setState((current) => ({ ...current, focused: ids || [] }));
    };

    const fetch = async (): Promise<void> => {
      try {
        setState((current) => ({ ...current, loading: true }));
        const { state } = getState();
        const filters = { ...state.filters };
        filters.from = new Dayts(filters.from).startOf('day').toISOString();
        filters.to = new Dayts(filters.to).endOf('day').add(1, 'day').toISOString();
        const res = await searchAddonTableWithMetadata(filters);
        const { imports, totalCount } = res;
        setState({ ...state, imports, total: totalCount, selected: [] });
      } catch (err) {
        console.error('Error fetching imports:', err);
      } finally {
        setState((current) => ({ ...current, loading: false }));
        await sleep(500);
        scrollToFocus();
      }
    };

    return {
      imports: [],
      total: 0,
      selected: [],
      focused: [],
      loading: false,
      filters: initFilters,
      table: initTable,
      modals: initModals,
      settings: initSettings,
      fetch,
      onSelect: (id: string): void => {
        // set focus
        setState((current) => ({
          ...current,
          selected: current.selected.includes(id) ? current.selected.filter((val) => val !== id) : [...current.selected, id],
        }));
        if (getState().state.selected.includes(id)) setFocus(id);
      },
      setFocus,
      scrollToFocus,
      onReset: (): void => {
        setState((current) => ({ ...current, filters: initFilters }));
        fetch();
      },
      expandedId: undefined,
    };
  };
  export type Query = {
    from: string;
    to: string;
    createdBy: string;
    airportCode: string[];
    providerId: string;
    type?: ImportWithAirportTypeEnum;
    status?: ImportWithAirportStatusEnum[];
    archived?: boolean;
  };
  export type Table = {
    sorting: {
      column: string | undefined;
      direction: 'asc' | 'desc' | undefined;
    };
  };
  export const initTable: Table = {
    sorting: {
      column: 'createdAt',
      direction: 'desc',
    },
  };
  export const ReadyToImportStatuses = [ImportStatusEnum.Potential, ImportStatusEnum.Staged, ImportStatusEnum.NotParsed];
}
